/**
 * StorageQuotaDialog — modal shown when a save/upload would exceed storage quota.
 *
 * Imperative API lives in `storageQuotaDialogBus.ts`. This file renders the
 * host and subscribes it to that bus.
 */

import { TIER_LABELS } from "@poseref/shared";
import { HardDrive } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "./Button.tsx";
import { ModalActions, ModalHeader, ModalShell } from "./ModalShell.tsx";
import {
  registerStorageQuotaDialogSink,
  type QuotaDialogArgs,
} from "./storageQuotaDialogBus.ts";
import {
  COLOR_ACCENT,
  COLOR_BORDER,
  COLOR_ERROR,
  COLOR_TEXT_SECONDARY,
  FONT_SIZE_BODY,
  FONT_SIZE_SMALL,
} from "./tokens.ts";

export function StorageQuotaDialog() {
  const [args, setArgs] = useState<QuotaDialogArgs | null>(null);

  useEffect(() => registerStorageQuotaDialogSink(setArgs), []);

  const close = useCallback(() => setArgs(null), []);

  if (!args) return null;
  return <StorageQuotaDialogBody {...args} onClose={close} />;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

// ---------------------------------------------------------------------------
// Body
// ---------------------------------------------------------------------------

function StorageQuotaDialogBody({
  usedBytes,
  limitBytes,
  tier,
  attemptedBytes,
  onClose,
}: QuotaDialogArgs & { onClose: () => void }) {
  const buttonRef = useRef<HTMLButtonElement>(null);

  // The bar's full width represents (used + attempted), which exceeds the limit.
  // The limit boundary is drawn as a vertical marker within the bar.
  const totalBytes = usedBytes + attemptedBytes;
  const usedPct = (usedBytes / totalBytes) * 100;
  const limitPct = (limitBytes / totalBytes) * 100;

  return (
    <ModalShell
      open
      onClose={onClose}
      onConfirm={onClose}
      width={400}
      autoFocusRef={buttonRef}
    >
      <ModalHeader
        icon={HardDrive}
        iconColor={COLOR_ERROR}
        title="Not Enough Space"
      />

      {/* Usage bar with overflow visualization */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {/* Bar container — relative so we can position the limit marker */}
        <div
          style={{
            position: "relative",
            height: 10,
            borderRadius: 5,
            background: COLOR_BORDER,
            overflow: "hidden",
          }}
        >
          {/* Current usage segment */}
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              height: "100%",
              width: `${usedPct}%`,
              background: COLOR_ACCENT,
              borderRadius: "5px 0 0 5px",
            }}
          />
          {/* Attempted addition segment — overflows past limit */}
          <div
            style={{
              position: "absolute",
              left: `${usedPct}%`,
              top: 0,
              height: "100%",
              width: `${100 - usedPct}%`,
              background: COLOR_ERROR,
              borderRadius: "0 5px 5px 0",
              opacity: 0.8,
            }}
          />
        </div>

        {/* Limit marker — positioned over the bar */}
        <div style={{ position: "relative", height: 0 }}>
          <div
            style={{
              position: "absolute",
              left: `${limitPct}%`,
              top: -14,
              width: 1.5,
              height: 14,
              background: COLOR_TEXT_SECONDARY,
              opacity: 0.6,
            }}
          />
        </div>

        {/* Labels */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: FONT_SIZE_SMALL,
            color: COLOR_TEXT_SECONDARY,
          }}
        >
          <span>
            {formatBytes(usedBytes)} used + {formatBytes(attemptedBytes)}
          </span>
          <span>
            {formatBytes(limitBytes)} limit ({TIER_LABELS[tier]})
          </span>
        </div>
      </div>

      {/* Message */}
      <div
        style={{
          fontSize: FONT_SIZE_BODY,
          color: COLOR_TEXT_SECONDARY,
          lineHeight: 1.5,
        }}
      >
        {tier === "basic"
          ? "You need more space than your Basic plan allows. Upgrade to Pro for 512 MB of storage, or delete unused items to free up space."
          : "You need more space than your Pro plan allows. Delete unused items from your library to free up space."}
      </div>

      {/* Dismiss button */}
      <ModalActions>
        <Button
          ref={buttonRef}
          onClick={onClose}
          variant="primary"
          style={{ background: COLOR_ERROR }}
        >
          OK
        </Button>
      </ModalActions>
    </ModalShell>
  );
}
